import { z } from "zod"
import { NamedEntitySchema } from "@/lib/schemas"
import { createStaticStore, StaticStore } from "@/lib/store-factory"
import { endpoints } from "@/config/endpoints.config"
import { Property } from "@/gen"
import { store } from "./stores"
import { client } from "@/hooks/useClient"
import { Node } from "@/nodes"
import { color } from "@uiw/react-codemirror"

// ███████╗ ██████╗██╗  ██╗███████╗███╗   ███╗ █████╗
// ██╔════╝██╔════╝██║  ██║██╔════╝████╗ ████║██╔══██╗
// ███████╗██║     ███████║█████╗  ██╔████╔██║███████║
// ╚════██║██║     ██╔══██║██╔══╝  ██║╚██╔╝██║██╔══██║
// ███████║╚██████╗██║  ██║███████╗██║ ╚═╝ ██║██║  ██║
// ╚══════╝ ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝     ╚═╝╚═╝  ╚═╝
//
export const CredentialSchema = NamedEntitySchema.extend({
  properties: z.record(z.string(), z.any()),
  type: z.string(),
})

export interface CredentialMeta {
  type: string
  label: string
  image_src?: string
  properties: Property[]
  color?: string
  node_type?: string
}

export type CredentialType = z.infer<typeof CredentialSchema>

// ███████╗████████╗ ██████╗ ██████╗ ███████╗
// ██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗██╔════╝
// ███████╗   ██║   ██║   ██║██████╔╝█████╗
// ╚════██║   ██║   ██║   ██║██╔══██╗██╔══╝
// ███████║   ██║   ╚██████╔╝██║  ██║███████╗
// ╚══════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚══════╝
//
export interface CredentialStore extends StaticStore<CredentialType> {
  credentialsMeta: {
    list: CredentialMeta[],
    record: Record<string, CredentialMeta>,
  }
  actions: {
    setCredentialsMetaFromNodes: (nodes: Node[]) => void
    testCredentials: (credential: Partial<CredentialType>) => Promise<void>
  } & StaticStore<CredentialType>["actions"]
}

export const credentialStore: CredentialStore = createStaticStore<CredentialType, any, any>({
  endpoint: endpoints.CREDENTIALS,
  credentialsMeta: {
    list: [],
    record: {},
  },
}, {
  setCredentialsMetaFromNodes: (nodes) => {
    const credentials = nodes?.filter(node => node.credentials?.length > 0).map((node) => node.credentials.map(cred => ({
      ...cred,
      image_src: node.image_src,
      node_type: node.type,
      color: node.color,
    }))).flat() || []
    store.credential.credentialsMeta = {
      list: credentials,
      record: credentials.reduce((acc, item) => {
        acc[item.type] = item
        return acc
      }, {} as Record<string, CredentialMeta>),
    }
  },
  testCredentials: (credential) => {
    return client.post(endpoints.CREDENTIALS + "/test", credential)
  },
})
